import * as React from "react"
import { MediaUploader } from "../../../ui/components/MediaUploader"
import type { MediaEntry } from "../../../ui/components/MediaUploader"
import { ConfirmModal } from "../../../ui/components/ConfirmModal"
import {
  fetchProductImages,
  uploadProductImageFile,
  uploadProductImageFromUrl,
  deleteProductImage,
  reorderProductImages,
  type ProductImage,
} from "../../lib/profile-avatar-upload"
import { useWebSocket } from "../../hooks/useWebSocket"
import { useI18n } from "../../lib/i18n"
import * as s from "./ProductImageGallery.css"

interface ProductImageGalleryProps {
  productId: string
  accessToken: string
  maxImages?: number
  /** Hide add/select/delete/reorder controls. */
  readOnly?: boolean
  onImageCountChange?: (count: number) => void
}

function StarIcon({ size = 9 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}

function toEntries(images: ProductImage[]): MediaEntry[] {
  return [...images]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((img) => ({
      id: img.id,
      url: img.thumbUrl ?? img.url,
      previewUrl: img.url,
    }))
}

export function ProductImageGallery({
  productId,
  accessToken,
  maxImages = 20,
  readOnly = false,
  onImageCountChange,
}: ProductImageGalleryProps) {
  const { t } = useI18n()
  const [images, setImages] = React.useState<MediaEntry[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [deleteTarget, setDeleteTarget] = React.useState<MediaEntry | null>(null)
  const [deleteLoading, setDeleteLoading] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)
  const [bulkDeleteIds, setBulkDeleteIds] = React.useState<string[] | null>(null)
  const [bulkDeleteLoading, setBulkDeleteLoading] = React.useState(false)
  const [bulkDeleteError, setBulkDeleteError] = React.useState<string | null>(null)

  React.useEffect(() => {
    onImageCountChange?.(images.length)
  }, [images.length, onImageCountChange])

  const { subscribe, unsubscribe } = useWebSocket()
  const [flashingIds, setFlashingIds] = React.useState<Map<string, number>>(() => new Map())
  const flashGenRef = React.useRef<Map<string, number>>(new Map())
  const imagesRef = React.useRef<MediaEntry[]>(images)
  imagesRef.current = images

  const reloadImages = React.useCallback(() => {
    fetchProductImages(accessToken, productId)
      .then((imgs) => {
        const next = toEntries(imgs)
        // Detect which images moved (position changed relative to previous order)
        const prevOrder = imagesRef.current.map(i => i.id)
        const nextOrder = next.map(i => i.id)
        const moved = nextOrder.filter((id, idx) => prevOrder[idx] !== id)
        if (moved.length > 0) {
          setFlashingIds(prev => {
            const map = new Map(prev)
            moved.forEach(id => {
              const gen = (flashGenRef.current.get(id) ?? 0) + 1
              flashGenRef.current.set(id, gen)
              map.set(id, gen)
            })
            return map
          })
          setTimeout(() => {
            setFlashingIds(prev => {
              const map = new Map(prev)
              moved.forEach(id => map.delete(id))
              return map
            })
          }, 1200)
        }
        setImages(next)
      })
      .catch(() => {})
  }, [accessToken, productId])

  React.useEffect(() => {
    setLoading(true)
    fetchProductImages(accessToken, productId)
      .then((imgs) => setImages(toEntries(imgs)))
      .catch(() => setImages([]))
      .finally(() => setLoading(false))
  }, [productId, accessToken])

  React.useEffect(() => {
    const channel = `entity:products:${productId}`
    subscribe([channel], reloadImages)
    return () => unsubscribe([channel], reloadImages)
  }, [productId, subscribe, unsubscribe, reloadImages])

  const uploadFile = React.useCallback(
    (file: File) => uploadProductImageFile(accessToken, productId, file),
    [accessToken, productId],
  )

  const uploadFromUrl = React.useCallback(
    (url: string) => uploadProductImageFromUrl(accessToken, productId, url),
    [accessToken, productId],
  )

  const handleChange = React.useCallback(
    (next: MediaEntry[]) => {
      // Detect a pure reorder: same IDs, same count, different order
      const prevIds = images.map((i) => i.id)
      const nextSettled = next.filter((i) => !i.uploading)
      const prevSettled = images.filter((i) => !i.uploading)
      const isReorder =
        nextSettled.length === prevSettled.length &&
        nextSettled.every((i) => prevIds.includes(i.id)) &&
        nextSettled.some((i, idx) => i.id !== (prevSettled[idx]?.id ?? ""))

      setImages(next)

      if (isReorder && nextSettled.length > 1) {
        reorderProductImages(accessToken, productId, nextSettled.map((i) => i.id)).catch(() => {})
      }
    },
    [images, accessToken, productId],
  )

  // Uploads are already fully persisted server-side by the time uploadFile()
  // resolves (the PUT /:id/images/file call creates the DB row and returns
  // { id, url, sortOrder }), so onApply just needs to sync local state — no
  // extra persistence call here.
  const handleApply = React.useCallback((next: MediaEntry[]) => {
    setImages(next)
  }, [])

  const handleDeleteRequest = React.useCallback(
    (id: string) => {
      const img = images.find((i) => i.id === id)
      if (img) setDeleteTarget(img)
    },
    [images],
  )

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      await deleteProductImage(accessToken, productId, deleteTarget.id)
      setImages((prev) => prev.filter((i) => i.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete image")
    } finally {
      setDeleteLoading(false)
    }
  }, [deleteTarget, accessToken, productId])

  const handleBulkDeleteRequest = React.useCallback((ids: string[]) => {
    setBulkDeleteIds(ids)
    setBulkDeleteError(null)
  }, [])

  const handleBulkDeleteConfirm = React.useCallback(async () => {
    if (!bulkDeleteIds?.length) return
    setBulkDeleteLoading(true)
    setBulkDeleteError(null)
    try {
      await Promise.all(bulkDeleteIds.map(id => deleteProductImage(accessToken, productId, id)))
      setImages((prev) => prev.filter((i) => !bulkDeleteIds.includes(i.id)))
      setBulkDeleteIds(null)
    } catch (err) {
      setBulkDeleteError(err instanceof Error ? err.message : "Failed to delete images")
    } finally {
      setBulkDeleteLoading(false)
    }
  }, [bulkDeleteIds, accessToken, productId])

  if (loading) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center", fontSize: 13, color: "#9CA3AF" }}>
        Loading images…
      </div>
    )
  }

  return (
    <>
      <MediaUploader
        items={images}
        onChange={handleChange}
        onApply={handleApply}
        uploadFile={uploadFile}
        uploadFromUrl={uploadFromUrl}
        maxItems={maxImages}
        pasteEnabled={!readOnly}
        selectable={!readOnly}
        readOnly={readOnly}
        onBulkDelete={readOnly ? undefined : handleBulkDeleteRequest}
        error={error}
        onError={setError}
        onDeleteRequest={readOnly ? undefined : handleDeleteRequest}
        placeholder={t("pro.products.dragDropImages")}
        hint={readOnly ? undefined : "Drag thumbnails to reorder"}
        flashingIds={flashingIds}
        renderBadge={(_entry, index) =>
          index === 0 ? (
            <div className={s.thumbMainBadge}>
              <StarIcon />
              Main
            </div>
          ) : null
        }
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title={t("pro.products.deleteImage")}
        body="This image will be permanently removed. This cannot be undone."
        confirmLabel={t("pro.products.deleteImage")}
        confirmColor="danger"
        loading={deleteLoading}
        error={deleteError}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null) }}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmModal
        open={bulkDeleteIds !== null}
        title={`Delete ${bulkDeleteIds?.length ?? 0} image${(bulkDeleteIds?.length ?? 0) !== 1 ? 's' : ''}`}
        body="The selected images will be permanently removed. This cannot be undone."
        confirmLabel={`Delete ${bulkDeleteIds?.length ?? 0} image${(bulkDeleteIds?.length ?? 0) !== 1 ? 's' : ''}`}
        confirmColor="danger"
        loading={bulkDeleteLoading}
        error={bulkDeleteError}
        onCancel={() => { setBulkDeleteIds(null); setBulkDeleteError(null) }}
        onConfirm={handleBulkDeleteConfirm}
      />
    </>
  )
}
